import { useCallback, useEffect, useState } from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { DropdownFrame } from "@/components/molecules/DropdownFrame";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/components/molecules/Nav";
import { Breakpoints } from "@/foundations/breakpoints/breakpoints";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useListingData } from "~/context/ListingPageDataContext";
import { IAttachment } from "~/types/api-types";

import { ListingSaveButton } from "../save/listing-save-button";
import { ListingShareButton } from "../share/listing-share-button";

type NavbarPageProps = {
  attachments: IAttachment[];
  hasAddons: boolean;
  hasReviews: boolean;
  showRecommendedVendors: boolean;
  toggleFavorite: () => void;
};

const NavbarPage = ({
  attachments,
  hasAddons,
  hasReviews,
  showRecommendedVendors,
  toggleFavorite,
}: NavbarPageProps) => {
  const { data: listingData } = useListingData();
  const { listingId } = listingData;

  const isMDScreen = useMediaQuery(`(min-width: ${Breakpoints.MD}px)`);

  const [open, setOpen] = useState<boolean>(false);

  const handleClick = (targetId: string) => (e: React.MouseEvent) => {
    e.preventDefault();

    if (targetId === "hero_section") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const element = document.getElementById(targetId);
    if (!element) return;

    const positionToScrollTo =
      element.getBoundingClientRect().top + window.scrollY - 72;

    window.scrollTo({
      top: positionToScrollTo,
      behavior: "smooth",
    });

    window.analyticsIntegrations.trackEvent("subnav clicked", {
      link_clicked: targetId,
    });
  };

  const navItems = [
    {
      id: "hero_section",
      label: (
        <FormattedMessage
          id="listing.listing_subnav.tab.photos"
          defaultMessage="Photos"
        />
      ),
    },
    {
      id: "about_section",
      label: (
        <FormattedMessage
          id="listing.listing_subnav.tab.about_this_space"
          defaultMessage="About this space"
        />
      ),
    },
    {
      id: "amenities_section",
      label: (
        <FormattedMessage
          id="listing.listing_subnav.tab.amenities"
          defaultMessage="Amenities"
        />
      ),
    },
    ...(showRecommendedVendors
      ? [
          {
            id: "vendors",
            label: <FormattedMessage defaultMessage="Vendors" />,
          },
        ]
      : []),
    ...(hasAddons
      ? [
          {
            id: "addons_section",
            label: (
              <FormattedMessage
                id="listing.listing_subnav.tab.add-ons"
                defaultMessage="Add-ons"
              />
            ),
          },
        ]
      : []),
    {
      id: "location_section",
      label: (
        <FormattedMessage
          id="listing.listing_subnav.tab.location"
          defaultMessage="Location"
        />
      ),
    },
    {
      id: "ophours_section",
      label: (
        <FormattedMessage
          id="listing.listing_subnav.tab.things_to_know"
          defaultMessage="Things to know"
        />
      ),
    },
    ...(hasReviews
      ? [
          {
            id: "reviews_section",
            label: (
              <FormattedMessage
                id="listing.listing_subnav.tab.reviews"
                defaultMessage="Reviews"
              />
            ),
          },
        ]
      : []),
  ];

  const handleScroll = useCallback(() => {
    if (window.scrollY > 200) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    // open NavDropdown when the page scrolls past a certain point
    // then close it when the page scrolls back up
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <DropdownFrame isVisible={open} setIsVisible={setOpen}>
      <NavigationMenu
        variant="white"
        className="tw-flex tw-border-b tw-border-gray-400 tw-overflow-x-auto"
      >
        <NavigationMenuList className="tw-gap-x-32">
          {navItems.map((link) => (
            <NavigationMenuItem key={link.id} className="tw-body-1-m">
              <Button
                onClick={handleClick(link.id)}
                className="tw-body-1-m hover:tw-text-gray-600"
                variant="text-only"
              >
                {link.label}
              </Button>
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
        <div className="tw-flex tw-items-center tw-ml-auto">
          <ListingShareButton
            appearance="white"
            category={listingData.category}
            emailShare={listingData.emailShare}
            fbShare={listingData.fbShare}
            isMDScreen={isMDScreen}
            origin="share listing details"
            metro={listingData.metro}
            twitterShare={listingData.twitterShare}
            useType={listingData.useType}
          />
          <ListingSaveButton
            isFav={
              !!attachments.find((attachment) => attachment.value === listingId)
            }
            listingId={listingId}
            toggleFavorite={toggleFavorite}
            appearance="black"
            isMDScreen={isMDScreen}
            variant="text-only"
          />
        </div>
      </NavigationMenu>
    </DropdownFrame>
  );
};

export default NavbarPage;
